import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  useClientCheckIns,
  CheckInAssignment,
  CheckInResponse,
} from "@/hooks/use-client-check-ins";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Loader2,
  Calendar,
  MessageSquare,
  Flag,
  FlagOff,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
} from "lucide-react";

interface CheckInResponsesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  assignment: CheckInAssignment | null;
}

interface ResponseWithAnswers extends CheckInResponse {
  answers?: Array<{
    id: string;
    question_id: string;
    answer_text: string | null;
    answer_number: number | null;
    question?: {
      question_text: string;
      question_type: string;
      order_index: number;
      scale_min?: number;
      scale_max?: number;
      scale_labels?: { min?: string; max?: string };
      options?: string[];
    };
  }>;
}

export function CheckInResponsesDialog({
  open,
  onOpenChange,
  clientId,
  assignment,
}: CheckInResponsesDialogProps) {
  const { updateResponse } = useClientCheckIns(clientId);
  const [responses, setResponses] = useState<ResponseWithAnswers[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch responses for this assignment
  useEffect(() => {
    if (!open || !assignment) {
      setResponses([]);
      setSelectedIndex(0);
      return;
    }

    const fetchResponses = async () => {
      setLoading(true);
      try {
        // Get all responses for this assignment
        const { data: responseData, error: responseError } = await supabase
          .from("check_in_responses")
          .select("*")
          .eq("assignment_id", assignment.id)
          .order("response_date", { ascending: false });

        if (responseError) throw responseError;

        // Get answers for each response
        const responsesWithAnswers = await Promise.all(
          (responseData || []).map(async (response) => {
            const { data: answers } = await supabase
              .from("check_in_answers")
              .select(`
                *,
                question:check_in_questions (
                  question_text,
                  question_type,
                  order_index,
                  scale_min,
                  scale_max,
                  scale_labels,
                  options
                )
              `)
              .eq("response_id", response.id);

            // Sort answers by question order
            const sortedAnswers = (answers || []).sort(
              (a: any, b: any) => (a.question?.order_index || 0) - (b.question?.order_index || 0)
            );

            return { ...response, answers: sortedAnswers };
          })
        );

        setResponses(responsesWithAnswers);
        setSelectedIndex(0);

        // Set initial feedback from first response
        if (responsesWithAnswers.length > 0) {
          setFeedback(responsesWithAnswers[0].trainer_feedback || "");
        }
      } catch (error) {
        console.error("Error fetching responses:", error);
        toast.error("Failed to load responses");
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, [open, assignment]);

  // Update feedback when selected response changes
  useEffect(() => {
    if (responses[selectedIndex]) {
      setFeedback(responses[selectedIndex].trainer_feedback || "");
    }
  }, [selectedIndex, responses]);

  const currentResponse = responses[selectedIndex];

  const handleSaveFeedback = async () => {
    if (!currentResponse) return;

    setSaving(true);
    try {
      await updateResponse(currentResponse.id, { trainer_feedback: feedback });

      // Update local state
      setResponses((prev) =>
        prev.map((r, i) =>
          i === selectedIndex
            ? { ...r, trainer_feedback: feedback, reviewed_at: new Date().toISOString() }
            : r
        )
      );
      toast.success("Feedback saved");
    } catch (error) {
      toast.error("Failed to save feedback");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFlag = async () => {
    if (!currentResponse) return;

    const newFlagged = !currentResponse.flagged;
    try {
      await updateResponse(currentResponse.id, { flagged: newFlagged });

      setResponses((prev) =>
        prev.map((r, i) =>
          i === selectedIndex ? { ...r, flagged: newFlagged } : r
        )
      );
      toast.success(newFlagged ? "Response flagged" : "Flag removed");
    } catch (error) {
      toast.error("Failed to update flag");
    }
  };

  const renderAnswer = (answer: ResponseWithAnswers["answers"][0]) => {
    const question = answer.question;
    if (!question) return null;

    const renderValue = () => {
      switch (question.question_type) {
        case "scale":
          return (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full"
                  style={{
                    width: `${
                      ((answer.answer_number || 0) - (question.scale_min || 1)) /
                      ((question.scale_max || 10) - (question.scale_min || 1)) *
                      100
                    }%`,
                  }}
                />
              </div>
              <span className="font-medium text-sm min-w-[2rem] text-right">
                {answer.answer_number}/{question.scale_max || 10}
              </span>
            </div>
          );

        case "checkbox":
          return (
            <div className="flex items-center gap-2">
              {answer.answer_number === 1 ? (
                <Badge variant="default" className="gap-1">
                  <Check className="h-3 w-3" />
                  Yes
                </Badge>
              ) : (
                <Badge variant="secondary">No</Badge>
              )}
            </div>
          );

        case "multiple_choice":
          return <Badge variant="outline">{answer.answer_text}</Badge>;

        case "number":
          return <span className="font-medium">{answer.answer_number}</span>;

        default:
          return (
            <p className="text-sm whitespace-pre-wrap">
              {answer.answer_text || <span className="text-muted-foreground italic">No response</span>}
            </p>
          );
      }
    };

    return (
      <div key={answer.id} className="space-y-1.5">
        <p className="text-sm font-medium">{question.question_text}</p>
        {renderValue()}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{assignment?.template?.name || "Check-in"} responses</DialogTitle>
          <DialogDescription>
            View and review client responses
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : responses.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">
              No responses yet. The client hasn't completed any check-ins.
            </p>
          </div>
        ) : (
          <>
            {/* Navigation */}
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                disabled={selectedIndex === responses.length - 1}
                onClick={() => setSelectedIndex((i) => i + 1)}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Older
              </Button>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">
                  {format(new Date(currentResponse.response_date), "MMMM d, yyyy")}
                </span>
                {currentResponse.reviewed_at && (
                  <Badge variant="outline" className="gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    Reviewed
                  </Badge>
                )}
                {currentResponse.flagged && (
                  <Badge variant="destructive" className="gap-1">
                    <Flag className="h-3 w-3" />
                    Flagged
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={selectedIndex === 0}
                onClick={() => setSelectedIndex((i) => i - 1)}
              >
                Newer
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <Separator />

            {/* Answers */}
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-2">
                {currentResponse.answers?.map((answer) => renderAnswer(answer))}
              </div>
            </ScrollArea>

            <Separator />

            {/* Trainer Feedback */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Trainer Feedback</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleFlag}
                  className={currentResponse.flagged ? "text-destructive" : ""}
                >
                  {currentResponse.flagged ? (
                    <>
                      <FlagOff className="h-4 w-4 mr-1" />
                      Remove Flag
                    </>
                  ) : (
                    <>
                      <Flag className="h-4 w-4 mr-1" />
                      Flag Response
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                placeholder="Add feedback for this response..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={2}
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleSaveFeedback}
                  disabled={saving || feedback === (currentResponse.trainer_feedback || "")}
                >
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Save Feedback
                </Button>
              </div>
            </div>

            {/* Response count indicator */}
            <div className="text-center text-xs text-muted-foreground">
              Response {selectedIndex + 1} of {responses.length}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
